import range from 'lodash/range';
import UAParser from 'ua-parser-js';
import { deviceEmulation } from './config';
import { keyframes } from 'styled-components';

export const borderColor = '#d4d4d4';
export const fitbitGreenText = '#00b0b9';
export const fitbitRedText = '#EF426F';

export const getOS = () => {
  if (deviceEmulation !== '') {
    if (deviceEmulation === 'iOS') {
      return 'iOS';
    }
    return deviceEmulation === 'windows' ? 'Windows' : 'Android';
  }
  const uaParser = new UAParser();
  if (uaParser.getOS().name === 'iOS') {
    return 'iOS';
  }
  return uaParser.getOS().name.startsWith('Windows') ? 'Windows' : 'Android';
};

export const isiOS = () => getOS() === 'iOS';
export const isWindows = () => getOS() === 'Windows';
export const getTheme = () => (isiOS() || isWindows()) ? 'iOS' : 'material';
export const isThemeiOS = () => getTheme() === 'iOS';

export const androidNavbarHeight = 56;
export const iOSNavbarHeight = 44;
const iOSButtonRadius = '5px';
const androidButtonRadius = '2px';

const iOSPulse = keyframes`
  50% { fill: #868686; }
  to { fill: rgba(134, 134, 134, .4) }
`;

const androidDashOffset = 187;
const androidLoadingDuration = '1.4s';

const androidDash = keyframes`
  0% {
    stroke-dashoffset: ${androidDashOffset};
  }
  50% {
    stroke-dashoffset: ${(androidDashOffset / 4).toFixed(2)};
    transform:rotate(135deg);
  }
  100% {
    stroke-dashoffset: ${androidDashOffset};
    transform:rotate(450deg);
  }
`;

const androidRotation = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(270deg); }
`;

export const themes = {
  iOS: {
    global: `
      html, body {
        font: 16px "Proxima Nova Regular";
        margin: 0;
        width: 100%;
        height: 100%;
        overflow: hidden;

        // Force GPU Accel
        transform: translate3d(0, 0, 0);
        -webkit-overflow-scrolling: touch;
        -webkit-backface-visibility: hidden;
        -webkit-transform-style: preserve-3d;
      }

      * {
        font-family: "Proxima Nova Regular";
        -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
        -webkit-touch-callout: none;
        user-select: none;
      }

      input {
        &, &:before, &:after {
          user-select: initial;
        }
      }

      *:focus {
        outline: none;
      }

      a {
        color: ${fitbitGreenText};
        text-decoration: none;
      }
    `,
    AutocompleteItems: `
      margin: 0;

      .section-body {
        border-top: none;
      }
    `,
    AutocompleteMargin: iOSNavbarHeight + 47, // 47px for SearchBar
    backgroundColor: '#efeff4',
    buttonRadius: iOSButtonRadius,
    Back: `
      .back-text {
        margin-left: -5px;
      }
    `,
    BackArrow: `
      height: 34px;
      margin-left: -5px;
      padding-bottom: 5px;
      padding-top: 5px;
      width: 34px;
    `,
    Button: ` 
      color: ${fitbitGreenText};
      padding: 10px;
      background: white;
      font-size: 18px;
      min-width: 24vw;
      text-align: center;
      &:disabled {
        opacity: 1;
        color: #999;
      }
    `,
    ClearIcon: `
      background: #CCCCCC;
      border-radius: 100px;
      color: white;
      font-size: 10px;
      height: 14px;
      line-height: 14px;
      width: 14px;
    `,
    List: `
      background: white;
    `,
    ListItem: `
      border-bottom: 1px solid #c8c7cc;
      padding: 12px 15px 12px 0;

      &:last-child {
        border-bottom: none;
      }
    `,
    LoadingIcon: `
      height: 27px;
      width: 27px;
      fill: #868686;

      path {
        ${
          range(12).map((value) =>
            `
              &:nth-of-type(${value + 1}) {
                animation: ${iOSPulse} 1s ${-0.083 * value}s infinite linear;
              }
            `
          ).join('')
        }
      }
    `,
    Modal: `
      background-color: #efeff4;
    `,
    SmartButton: `
      font-size: 18px;
      background-color: white;  
      border: 0;
      display: flex;
      flex-wrap: wrap;
      justify-content: space-between;
    `,
    SmartButtonLabel: `
      color: #2ab7be;
    `,
    SmartButtonDisabled: `
      color: #A6A6A6;
    `,
    SmartViewNavbar: `
      align-items: center;
      background-color: white;
      box-sizing: border-box;
      border-bottom: 1px solid ${borderColor};
      color: ${fitbitGreenText};
      font-size: 18px;
      height: ${iOSNavbarHeight}px;
      line-height: ${iOSNavbarHeight}px;
      width: 100%;
      z-index: 65535;
    `,
    ToggleEditButton: `
      color: ${fitbitRedText};
      border-top: 1px solid #c8c7cc;
      margin-left: 0 !important;
    `,
    ToggleWrapper: `
      font-size: 18px;
    `,
    Navbar: `
      align-items: center;
      background-color: white;
      border-bottom: 1px solid ${borderColor};
      box-sizing: border-box;
      color: ${fitbitGreenText};
      font-size: 18px;
      height: ${iOSNavbarHeight}px;
      left: 0;
      line-height: ${iOSNavbarHeight}px;
      position: absolute;
      top: 0;
      width: 100%;
      z-index: 65535;
    `,
    NavbarHeight: iOSNavbarHeight,
    NavBack: {
      'min-width': '25%'
    },
    NavCenter: `
      font-family: 'Proxima Nova Semibold';
      flex-grow: 1;
      text-align: center;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    `,
    NavCenterContainer: `
      background: #f3f3f3;
      border-radius: 29px;
    `,
    NavCenterItem: ({ active }) => `
      color: #9B9B9B;
      line-height: 28px;
      min-width: 100px;

      ${
        active ? `
          background: white;
          border: 1px solid #f3f3f3;
          border-radius: 29px;
          color: #01b1ba;
        ` : ''
      }
    `,
    NavRight: `
      min-width: 25%;
      text-align: right;
    `,
    SearchBar: ({ autocomplete }) => `
      padding: 12px 15px 12px;
    `,
    SearchBarInner: `
      > input {
        line-height: 24px;
        font-size: 18px;
      }
    `,
    SearchIcon: () =>`
      margin-right: 5px;
      > svg {
        height: 21px;
        width: 21px;
        g, path {
          stroke: #C3C8C9;
        }
      }
    `,
    SectionContainer: `
      margin-top: 35px;
    `,
    SectionBody: `
      background: white;
      border-top: 1px solid #c8c7cc;
      border-bottom: 1px solid #c8c7cc;

      .content-block + .content-block {
        border-top: 1px solid #c8c7cc;
      }
    `,
    Value: `
      display: inline-block;
      flex: 1;
      font-size: 18px;
      text-align: right;
    `,
    ValueWrapper: `
      display: flex;
      flex: 1;
      min-width: 0;
    `
  },
  material: {
    global: `
      html, body {
        font: 16px "Proxima Nova Regular";
        color: #272727;
        width: 100%;
        height: 100%;
        margin: 0;
      }

      * {
        font-family: "Proxima Nova Regular";
        user-select: none;
      }

      *:focus {
        outline: none;
      }

      a {
        color: ${fitbitGreenText};
        text-decoration: none;
      }
    `,
    AutocompleteItems: `
      margin-top: 32px;
    `,
    AutocompleteMargin: androidNavbarHeight + 48, // 48px for SearchBar
    backgroundColor: 'white',
    buttonRadius: androidButtonRadius,
    Back: `
    `,
    BackArrow: `
      height: 24px;
      margin-bottom: 16px;
      position: relative;
      margin-left: 5px;
      margin-top: 16px;
      width: 24px;
      > svg {
        position: absolute;
        top: 0;
      }
    `,
    Button: `
      width: 100%;
      color: ${fitbitRedText};
    `,
    ClearIcon: `
      > svg {
        fill: ${fitbitGreenText};
        height: 24px;
        width: 24px;
      }
    `,
    List: `
    `,
    ListItem: `
      padding: 16px;

      &:first-child {
        padding-top: 0;
      }
    `,
    LoadingIcon: `
      height: 40px;
      width: 40px;
      animation: ${androidRotation} ${androidLoadingDuration} linear infinite;

      circle {
        stroke: #00B0B9;
        stroke-dasharray: ${androidDashOffset};
        stroke-dashoffset: 0;
        transform-origin: center;
        animation: ${androidDash} ${androidLoadingDuration} ease-in-out infinite;
      }
    `,
    Modal: `
      background-color: white;
    `,
    SmartButton: `
      color: #272727;
      background-color: white;  
      border: 0;
      font-size: 16px;
      text-align: left;
    `,
    SmartButtonLabel: `
      color: ${fitbitRedText};
    `,
    SmartButtonDisabled: `
      color: #A6A6A6;
    `,
    SmartViewNavbar: `
      background-color: white;
      color: ${fitbitGreenText};
      font-size: 20px;
      height: ${androidNavbarHeight}px;
      line-height: ${androidNavbarHeight}px;
      min-height: ${androidNavbarHeight}px;
      width: 100%;
    `,
    ToggleEditButton: `
      font-size: 14px;
      color: ${fitbitRedText};
      float: right;
      height: 20px;
      line-height: 20px;
      min-width: 50px;
      width: inherit;
      padding: 0;
      text-transform: uppercase;

      .section-body & {
        margin-top: -32px;
      }
    `,
    ToggleWrapper: `
      font-size: 16px;
    `,
    Navbar: `
      background-color: white;
      color: ${fitbitGreenText};
      font: 20px 'Proxima Nova Semibold';
      height: ${androidNavbarHeight}px;
      line-height: ${androidNavbarHeight}px;
      min-height: ${androidNavbarHeight}px;
      position: absolute;
      top: 0;
      width: 100%;
      &.scrolling {
        box-shadow: 0 2px 4px -1px rgba(0,0,0,.2), 0 4px 5px 0 rgba(0,0,0,.14), 0 1px 10px 0 rgba(0,0,0,.12);
        z-index: 1000;
      }
    `,
    NavbarHeight: androidNavbarHeight,
    NavBack: `
      width: 72px;
      margin-left: 15px;
      > div {
        height: ${androidNavbarHeight}px;
      }
      .back-text {
        display: none;
      }
    `,
    NavCenter: `
      flex-grow: 1;
      line-height: ${androidNavbarHeight}px;
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    `,
    NavCenterContainer: '',
    NavCenterItem: ({ active }) => `
      color: black;
      font-family: 'Proxima Nova Bold';
      height: ${androidNavbarHeight}px;
      line-height: ${androidNavbarHeight}px;
      text-transform: uppercase;
      border-bottom: 0.5px solid #D4D4D4;

      ${active ? `
        color: #01b1ba;
        border-bottom: 2px solid ${fitbitGreenText};
      ` : ''}
    `,
    NavRight: `
      text-align: right;
      width: 20%;
    `,
    SearchBar: ({ autocomplete }) => autocomplete && `
      position: relative;
      box-shadow: 0 2px 4px -1px rgba(0,0,0,.2);
      z-index: 1;

      // CSS Specificity Hack
      // https://www.styled-components.com/docs/faqs#how-can-i-override-styles-with-higher-specificity
      &&& {
        padding: 12px 24px 8px;
      }
    `,
    SearchBarInner: `
      border-color: #272727;
      border-bottom: 1px solid ${fitbitGreenText};
      > * {
        border: none;
      }
      > input {
        line-height: 24px;
        font-size: 16px;
      }
    `,
    SearchIcon: ({ focus }) =>`
      margin-right: 10px;
      > svg {
        height: 21px;
        width: 21px;
        g, path {
          stroke: ${focus ? 'black' : '#C3C8C9'};
        }
      }
    `,
    SectionContainer: `
      margin-top: 32px;
    `,
    SectionBody: `
      border-bottom: 1px solid #e0e0e0;
      padding-bottom: 16px;

      > .content-block:first-of-type {
        padding-top: 0;
      }
    `,
    SelectValue: `
      text-align: left;
      width: 90vw;
    `,
    TextImageRowIcon: `
      border-radius: 100px;
    `,
    Value: `
    `,
    ValueWrapper: `
      font-size: 14px;
      color: #777777;
    `
  }
};

export default themes[getTheme()];



// WEBPACK FOOTER //
// ./src/themes.js